import React, { useState, useEffect, useMemo } from 'react';
import { 
  getTickets, 
  getTicketStats, 
  exportTickets,
  getITUsers,
  getCategories,
  getSystems,
  getHelpdeskDepartments,
  getAllUsers,
  getDivisionDepartments
} from '../../../models/helpdeskModel';
import SearchableSelect from '../../../components/common/SearchableSelect';
import CustomDatePicker from '../../../components/common/CustomDatePicker';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  AreaChart,
  Area,
  Legend,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ComposedChart
} from 'recharts';

function HelpdeskReports() {
  const [tickets, setTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview'); // overview, performance, analytics, tickets
  const [timeRange, setTimeRange] = useState('30'); // 7, 30, 90, 365, all
  const [showCustomDateModal, setShowCustomDateModal] = useState(false);
  const [divisions, setDivisions] = useState([]);
  const [allDivisions, setAllDivisions] = useState([]); // Store all divisions
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [itUsers, setItUsers] = useState([]);
  const [userDepartmentMap, setUserDepartmentMap] = useState({});
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    category: '',
    assignee: '',
    requester: '',
    department: '',
    division: '',
    dateRange: {
      start: '',
      end: ''
    }
  });

  useEffect(() => {
    fetchTickets();
    loadDepartments();
    loadDivisions();
    loadUsers();
    loadItUsers();
  }, []);

  const loadDepartments = async () => {
    try {
      const response = await getDivisionDepartments();
      if (response && response.status === 1 && response.data && response.data.division_departments) {
        setDepartments(response.data.division_departments);
      }
    } catch (error) {
      console.error('Error loading departments:', error);
      setDepartments([]);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await getAllUsers();
      if (response && response.status === 1 && response.data && response.data.users) {
        const usersData = response.data.users;
        setUsers(usersData);
        
        // Create a map of user_id/user_email to department for quick lookup
        const deptMap = {};
        usersData.forEach(user => {
          if (user.user_id) {
            deptMap[user.user_id] = user.department;
          }
          if (user.user_email) {
            deptMap[user.user_email.toLowerCase()] = user.department;
          }
        });
        setUserDepartmentMap(deptMap);
      }
    } catch (error) {
      console.error('Error loading users:', error);
      setUsers([]);
      setUserDepartmentMap({});
    }
  };

  const loadItUsers = async () => {
    try {
      const response = await getITUsers();
      if (response && response.status === 1 && response.data) {
        // API returns data.agents array
        const agents = response.data.agents || response.data;
        setItUsers(Array.isArray(agents) ? agents : []);
      }
    } catch (error) {
      console.error('Error loading IT users:', error);
      setItUsers([]);
    }
  };

  const loadDivisions = async () => {
    try {
      const response = await getHelpdeskDepartments();
      if (response && response.status === 1 && response.data && response.data.departments) {
        const divisionsData = response.data.departments;
        setAllDivisions(divisionsData);
        setDivisions(divisionsData); // Initially show all
      }
    } catch (error) {
      console.error('Error loading divisions:', error);
      setAllDivisions([]);
      setDivisions([]);
    }
  };

  // Filter divisions when department filter changes
  useEffect(() => {
    if (filters.department) {
      // Filter divisions to only show those belonging to the selected department
      const filteredDivisions = allDivisions.filter(div => 
        String(div.division_department) === String(filters.department)
      );
      setDivisions(filteredDivisions);
      
      // Clear division filter if current selection is not in filtered list
      if (filters.division && !filteredDivisions.find(d => String(d.department_id) === String(filters.division))) {
        setFilters(prev => ({ ...prev, division: '' }));
      }
    } else {
      // Show all divisions when no department is selected
      setDivisions(allDivisions);
    }
  }, [filters.department, allDivisions]);

  // Apply time range filter when timeRange changes
  useEffect(() => {
    if (timeRange !== 'all' && timeRange !== 'custom' && Array.isArray(tickets)) {
      const cutoffDate = new Date();
      
      if (timeRange === 'thisMonth') {
        // This month - from 1st of current month to now
        cutoffDate.setDate(1);
        cutoffDate.setHours(0, 0, 0, 0);
      } else if (timeRange === 'lastMonth') {
        // Last month - from 1st of last month to last day of last month
        const endDate = new Date();
        endDate.setDate(0); // Last day of previous month
        endDate.setHours(23, 59, 59, 999);
        
        cutoffDate.setMonth(cutoffDate.getMonth() - 1);
        cutoffDate.setDate(1);
        cutoffDate.setHours(0, 0, 0, 0);
        
        // Apply the time range filter for last month
        const filtered = tickets.filter(ticket => {
          const ticketDate = new Date(ticket.created_at || ticket.date_created);
          return ticketDate >= cutoffDate && ticketDate <= endDate;
        });
        
        setFilters(prev => ({
          ...prev,
          dateRange: {
            start: cutoffDate.toISOString().split('T')[0],
            end: endDate.toISOString().split('T')[0]
          }
        }));
        return;
      } else {
        // Specific number of days (7, 30, etc.)
        const days = parseInt(timeRange);
        cutoffDate.setDate(cutoffDate.getDate() - days);
      }
      
      // Apply the time range filter
      const filtered = tickets.filter(ticket => {
        const ticketDate = new Date(ticket.created_at || ticket.date_created);
        return ticketDate >= cutoffDate;
      });
      
      setFilters(prev => ({
        ...prev,
        dateRange: {
          start: cutoffDate.toISOString().split('T')[0],
          end: new Date().toISOString().split('T')[0]
        }
      }));
    } else if (timeRange === 'all' || timeRange === 'custom') {
      // Clear date range filter for 'all' or keep existing for 'custom'
      if (timeRange === 'all') {
        setFilters(prev => ({
          ...prev,
          dateRange: { start: '', end: '' }
        }));
      }
    }
  }, [timeRange, tickets]);

  useEffect(() => {
    // Only apply filters if tickets is an array
    if (Array.isArray(tickets)) {
      applyFilters();
    }
  }, [tickets, filters]);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const response = await getTickets();
      console.log('Fetched tickets:', response);
      
      let ticketsData = [];
      
      // Handle nested data structure: response.data.data.tickets
      if (response && response.data && response.data.data && Array.isArray(response.data.data.tickets)) {
        ticketsData = response.data.data.tickets;
      } else if (response && response.data && Array.isArray(response.data.tickets)) {
        ticketsData = response.data.tickets;
      } else if (response && response.data && Array.isArray(response.data)) {
        ticketsData = response.data;
      } else if (Array.isArray(response)) {
        ticketsData = response;
      } else if (response && typeof response === 'object' && response.tickets && Array.isArray(response.tickets)) {
        ticketsData = response.tickets;
      } else {
        console.warn('Unexpected ticket data format:', response);
        ticketsData = [];
      }
      
      // Map backend field names to frontend expected names
      ticketsData = ticketsData.map(ticket => ({
        ...ticket,
        requester: ticket.requester_name_resolved || ticket.requester_name || ticket.requester || ticket.created_by,
        assignee: ticket.assigned_name || ticket.assignee || ticket.assigned_to
      }));
      
      setTickets(ticketsData);
    } catch (error) {
      console.error('Error fetching tickets:', error);
      setTickets([]);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    // Ensure tickets is an array before filtering
    if (!Array.isArray(tickets)) {
      setFilteredTickets([]);
      return;
    }
    
    let filtered = [...tickets];

    // Status filter
    if (filters.status) {
      filtered = filtered.filter(ticket => 
        String(ticket.status || '').toLowerCase() === filters.status.toLowerCase()
      );
    }

    // Priority filter
    if (filters.priority) {
      filtered = filtered.filter(ticket => 
        String(ticket.priority || '').toLowerCase() === filters.priority.toLowerCase()
      );
    }

    // Category filter
    if (filters.category) {
      filtered = filtered.filter(ticket => 
        String(ticket.category || '').toLowerCase().includes(filters.category.toLowerCase())
      );
    }

    // Assignee filter
    if (filters.assignee) {
      filtered = filtered.filter(ticket => 
        String(ticket.assigned_to || '') === String(filters.assignee)
      );
    }

    // Requester filter
    if (filters.requester) {
      filtered = filtered.filter(ticket =>
        String(ticket.requester || ticket.created_by || '').toLowerCase().includes(filters.requester.toLowerCase())
      );
    }

    // Department filter - match by requester's department_id using user mapping
    if (filters.department) {
      filtered = filtered.filter(ticket => {
        // Get the user's department (division) first
        let requesterDivisionId = null;
        
        if (ticket.requester_id && userDepartmentMap[ticket.requester_id]) {
          requesterDivisionId = userDepartmentMap[ticket.requester_id];
        } else if (ticket.requester_email && userDepartmentMap[ticket.requester_email.toLowerCase()]) {
          requesterDivisionId = userDepartmentMap[ticket.requester_email.toLowerCase()];
        }
        
        // Find the division to get its parent department
        if (requesterDivisionId) {
          const division = allDivisions.find(d => String(d.department_id) === String(requesterDivisionId));
          if (division && division.division_department) {
            return String(division.division_department) === String(filters.department);
          }
        }
        
        return false;
      });
    }

    // Division filter - match by requester's department using user mapping
    if (filters.division) {
      filtered = filtered.filter(ticket => {
        // Try to get department from requester_id or requester_email
        let requesterDepartment = null;
        
        if (ticket.requester_id && userDepartmentMap[ticket.requester_id]) {
          requesterDepartment = String(userDepartmentMap[ticket.requester_id]);
        } else if (ticket.requester_email && userDepartmentMap[ticket.requester_email.toLowerCase()]) {
          requesterDepartment = String(userDepartmentMap[ticket.requester_email.toLowerCase()]);
        }
        
        return requesterDepartment === String(filters.division);
      });
    }    // Date range filter
    if (filters.dateRange.start) {
      const startDate = new Date(filters.dateRange.start);
      filtered = filtered.filter(ticket => {
        const ticketDate = new Date(ticket.created_at || ticket.date_created);
        return ticketDate >= startDate;
      });
    }

    if (filters.dateRange.end) {
      const endDate = new Date(filters.dateRange.end);
      endDate.setHours(23, 59, 59, 999); // Include the entire end date
      filtered = filtered.filter(ticket => {
        const ticketDate = new Date(ticket.created_at || ticket.date_created);
        return ticketDate <= endDate;
      });
    }

    setFilteredTickets(filtered);
  };

  const handleFilterChange = (key, value) => {
    if (key.includes('.')) {
      const [parent, child] = key.split('.');
      setFilters(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFilters(prev => ({
        ...prev,
        [key]: value
      }));
    }
  };

  const handleExport = async () => {
    try {
      if (!Array.isArray(filteredTickets) || filteredTickets.length === 0) {
        alert('No tickets to export. Please adjust your filters.');
        return;
      }

      // Send the filtered ticket IDs to export only what's visible
      const ticketIds = filteredTickets.map(t => t.ticket_id || t.id).filter(Boolean);
      
      const response = await exportTickets({ 
        ...filters,
        ticket_ids: ticketIds 
      });
      
      // Create a blob and download the file
      const blob = new Blob([response], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `helpdesk-report-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error exporting report:', error);
      alert('Failed to export report. Please try again.');
    }
  };

  const formatDateTime = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleString();
    } catch (error) {
      return dateStr;
    }
  };

  const getStatusColor = (status) => {
    const normalizeStatus = (status) => {
      if (typeof status === 'number') {
        const statusMap = {
          1: 'new',
          2: 'closed', 
          3: 'escalated',
          10: 'assigned'
        };
        return statusMap[status] || 'new';
      }
      return String(status || '').toLowerCase();
    };

    const colors = {
      'open': 'bg-blue-100 text-blue-800',
      'new': 'bg-green-100 text-green-800',
      'in_progress': 'bg-yellow-100 text-yellow-800',
      'pending': 'bg-orange-100 text-orange-800',
      'resolved': 'bg-purple-100 text-purple-800',
      'closed': 'bg-gray-100 text-gray-800',
      'cancelled': 'bg-red-100 text-red-800',
      'assigned': 'bg-blue-100 text-blue-800',
      'escalated': 'bg-red-100 text-red-800'
    };
    return colors[normalizeStatus(status)] || 'bg-gray-100 text-gray-800';
  };

  const getPriorityColor = (priority) => {
    const normalizePriority = (priority) => {
      if (typeof priority === 'number') {
        const priorityMap = {
          1: 'low',
          2: 'medium',
          3: 'high',
          4: 'urgent'
        };
        return priorityMap[priority] || 'medium';
      }
      return String(priority || 'medium').toLowerCase();
    };

    const colors = {
      'critical': 'bg-red-100 text-red-800',
      'high': 'bg-orange-100 text-orange-800',
      'medium': 'bg-yellow-100 text-yellow-800',
      'low': 'bg-green-100 text-green-800',
      'urgent': 'bg-red-100 text-red-800'
    };
    return colors[normalizePriority(priority)] || 'bg-gray-100 text-gray-800';
  };

  // Chart data processing functions
  const getStatusDistribution = () => {
    if (!Array.isArray(filteredTickets)) return [];
    
    const statusCounts = {};
    filteredTickets.forEach(ticket => {
      const status = ticket.status || 'unknown';
      statusCounts[status] = (statusCounts[status] || 0) + 1;
    });
    
    return Object.entries(statusCounts).map(([status, count]) => ({
      status: status.charAt(0).toUpperCase() + status.slice(1),
      count,
      percentage: ((count / filteredTickets.length) * 100).toFixed(1)
    }));
  };

  const getPriorityDistribution = () => {
    if (!Array.isArray(filteredTickets)) return [];
    
    const priorityCounts = {};
    filteredTickets.forEach(ticket => {
      const priority = ticket.priority || 'medium';
      priorityCounts[priority] = (priorityCounts[priority] || 0) + 1;
    });
    
    return Object.entries(priorityCounts).map(([priority, count]) => ({
      priority: priority.charAt(0).toUpperCase() + priority.slice(1),
      count,
      percentage: ((count / filteredTickets.length) * 100).toFixed(1)
    }));
  };

  const getTicketsTrend = () => {
    if (!Array.isArray(filteredTickets)) return [];
    
    const last30Days = [];
    for (let i = 29; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      last30Days.push({
        date: date.toISOString().split('T')[0],
        count: 0,
        resolved: 0
      });
    }
    
    filteredTickets.forEach(ticket => {
      const ticketDate = new Date(ticket.created_at || ticket.date_created);
      const dateStr = ticketDate.toISOString().split('T')[0];
      const dayData = last30Days.find(day => day.date === dateStr);
      if (dayData) {
        dayData.count++;
        if (ticket.status === 'resolved' || ticket.status === 'closed' || ticket.status === 'completed') {
          dayData.resolved++;
        }
      }
    });
    
    return last30Days.map(day => ({
      ...day,
      date: new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    }));
  };

  const getAssigneeWorkload = () => {
    if (!Array.isArray(filteredTickets)) return [];
    
    const assigneeCounts = {};
    filteredTickets.forEach(ticket => {
      const assignee = ticket.assigned_to || ticket.assignee || 'Unassigned';
      if (!assigneeCounts[assignee]) {
        assigneeCounts[assignee] = { total: 0, open: 0, resolved: 0 };
      }
      assigneeCounts[assignee].total++;
      if (ticket.status === 'resolved' || ticket.status === 'closed' || ticket.status === 'completed') {
        assigneeCounts[assignee].resolved++;
      } else {
        assigneeCounts[assignee].open++;
      }
    });
    
    return Object.entries(assigneeCounts)
      .map(([assignee, counts]) => ({
        assignee: assignee.length > 15 ? assignee.substring(0, 15) + '...' : assignee,
        total: counts.total,
        open: counts.open,
        resolved: counts.resolved
      }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 10); // Top 10 assignees
  };

  const getCategoryBreakdown = () => {
    if (!Array.isArray(filteredTickets)) return [];
    
    const categoryCounts = {};
    filteredTickets.forEach(ticket => {
      const category = ticket.category || ticket.department || 'Other';
      categoryCounts[category] = (categoryCounts[category] || 0) + 1;
    });
    
    return Object.entries(categoryCounts)
      .map(([category, count]) => ({
        category: category.length > 20 ? category.substring(0, 20) + '...' : category,
        count,
        percentage: ((count / filteredTickets.length) * 100).toFixed(1)
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 8); // Top 8 categories
  };

  // Chart colors
  const CHART_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#84CC16', '#F97316'];
  const STATUS_COLORS = {
    'Open': '#3B82F6',
    'New': '#10B981', 
    'In_progress': '#F59E0B',
    'Pending': '#F97316',
    'Resolved': '#8B5CF6',
    'Closed': '#6B7280',
    'Cancelled': '#EF4444',
    'Assigned': '#06B6D4',
    'Escalated': '#DC2626'
  };

  const getStatusIcon = (status) => {
    const normalizeStatus = (status) => {
      if (typeof status === 'number') {
        const statusMap = {
          1: 'new',
          2: 'closed', 
          3: 'escalated',
          10: 'assigned'
        };
        return statusMap[status] || 'new';
      }
      return String(status || '').toLowerCase();
    };

    const normalizedStatus = normalizeStatus(status);
    switch (normalizedStatus) {
      case 'resolved':
      case 'closed':
        return (
          <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        );
      case 'cancelled':
        return (
          <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        );
      case 'in_progress':
      case 'assigned':
        return (
          <svg className="w-4 h-4 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'escalated':
        return (
          <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.936-.833-2.707 0L3.107 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        );
      default:
        return (
          <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
    }
  };

    const getAverageResolutionTime = () => {
    if (!Array.isArray(filteredTickets)) return 'N/A';
    
    const resolvedTickets = filteredTickets.filter(ticket => 
      ticket.status === 'resolved' || ticket.status === 'closed' || ticket.status === 'completed'
    );
    
    if (resolvedTickets.length === 0) return 'N/A';
    
    const totalTime = resolvedTickets.reduce((sum, ticket) => {
      const created = new Date(ticket.created_at || ticket.date_created);
      const resolved = new Date(ticket.resolved_at || ticket.updated_at);
      return sum + (resolved - created);
    }, 0);
    
    const avgTime = totalTime / resolvedTickets.length;
    const hours = Math.round(avgTime / (1000 * 60 * 60));
    
    return `${hours}h`;
  };

  // Advanced Analytics Functions
  const getResolutionTimeByPriority = () => {
    if (!Array.isArray(filteredTickets)) return [];
    
    const priorities = ['critical', 'high', 'medium', 'low'];
    return priorities.map(priority => {
      const priorityTickets = filteredTickets.filter(ticket => 
        String(ticket.priority || 'medium').toLowerCase() === priority &&
        (ticket.status === 'resolved' || ticket.status === 'closed' || ticket.status === 'completed')
      );
      
      if (priorityTickets.length === 0) {
        return { priority: priority.charAt(0).toUpperCase() + priority.slice(1), avgHours: 0, count: 0 };
      }
      
      const totalTime = priorityTickets.reduce((sum, ticket) => {
        const created = new Date(ticket.created_at || ticket.date_created);
        const resolved = new Date(ticket.resolved_at || ticket.updated_at);
        return sum + (resolved - created);
      }, 0);
      
      const avgTime = totalTime / priorityTickets.length;
      const avgHours = Math.round(avgTime / (1000 * 60 * 60));
      
      return {
        priority: priority.charAt(0).toUpperCase() + priority.slice(1),
        avgHours,
        count: priorityTickets.length
      };
    });
  };

  const getSLAPerformance = () => {
    if (!Array.isArray(filteredTickets)) return { met: 0, breached: 0, pending: 0 };
    
    const slaTargets = {
      critical: 4,
      high: 8,
      medium: 24,
      low: 48
    };
    
    let met = 0;
    let breached = 0;
    let pending = 0;
    
    filteredTickets.forEach(ticket => {
      const priority = String(ticket.priority || 'medium').toLowerCase();
      const targetHours = slaTargets[priority] || 24;
      const created = new Date(ticket.created_at || ticket.date_created);
      const now = new Date();
      const hoursElapsed = (now - created) / (1000 * 60 * 60);
      
      if (ticket.status === 'resolved' || ticket.status === 'closed' || ticket.status === 'completed') {
        const resolved = new Date(ticket.resolved_at || ticket.updated_at);
        const resolutionHours = (resolved - created) / (1000 * 60 * 60);
        if (resolutionHours <= targetHours) {
          met++;
        } else {
          breached++;
        }
      } else {
        if (hoursElapsed > targetHours) {
          breached++;
        } else {
          pending++;
        }
      }
    });
    
    return { met, breached, pending };
  };

  const getHourlyDistribution = () => {
    if (!Array.isArray(filteredTickets)) return [];
    
    const hours = Array.from({ length: 24 }, (_, i) => ({
      hour: `${i.toString().padStart(2, '0')}:00`,
      count: 0
    }));
    
    filteredTickets.forEach(ticket => {
      const created = new Date(ticket.created_at || ticket.date_created);
      const hour = created.getHours();
      hours[hour].count++;
    });
    
    return hours;
  };

  const getDailyDistribution = () => {
    if (!Array.isArray(filteredTickets)) return [];
    
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const distribution = days.map(day => ({ day, count: 0 }));
    
    filteredTickets.forEach(ticket => {
      const created = new Date(ticket.created_at || ticket.date_created);
      const dayIndex = created.getDay();
      distribution[dayIndex].count++;
    });
    
    return distribution;
  };

  const getTopIssues = () => {
    if (!Array.isArray(filteredTickets)) return [];
    
    const issues = {};
    
    filteredTickets.forEach(ticket => {
      const subject = ticket.subject || ticket.title || 'Unknown Issue';
      const key = subject.substring(0, 50); // Group similar issues by first 50 chars
      issues[key] = (issues[key] || 0) + 1;
    });
    
    return Object.entries(issues)
      .map(([issue, count]) => ({ issue, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  };

  const getAssigneePerformance = () => {
    if (!Array.isArray(filteredTickets)) return [];
    
    const performance = {};
    
    filteredTickets.forEach(ticket => {
      const assignee = ticket.assigned_to || ticket.assignee || 'Unassigned';
      
      if (!performance[assignee]) {
        performance[assignee] = {
          assignee,
          total: 0,
          resolved: 0,
          avgResolutionTime: 0,
          totalResolutionTime: 0,
          resolvedCount: 0
        };
      }
      
      performance[assignee].total++;
      
      if (ticket.status === 'resolved' || ticket.status === 'closed' || ticket.status === 'completed') {
        performance[assignee].resolved++;
        performance[assignee].resolvedCount++;
        
        const created = new Date(ticket.created_at || ticket.date_created);
        const resolved = new Date(ticket.resolved_at || ticket.updated_at);
        const resolutionTime = (resolved - created) / (1000 * 60 * 60);
        performance[assignee].totalResolutionTime += resolutionTime;
      }
    });
    
    return Object.values(performance)
      .map(p => ({
        name: p.assignee,
        total: p.total,
        resolved: p.resolved,
        avgResolutionTime: p.resolvedCount > 0 
          ? Math.round(p.totalResolutionTime / p.resolvedCount) + ' hrs'
          : '0 hrs',
        resolutionRate: p.total > 0 
          ? Math.round((p.resolved / p.total) * 100) 
          : 0
      }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 10);
  };

  const getCategoryPerformance = () => {
    if (!Array.isArray(filteredTickets)) return [];
    
    const categories = {};
    
    filteredTickets.forEach(ticket => {
      const category = ticket.category || ticket.department || 'Other';
      
      if (!categories[category]) {
        categories[category] = {
          category,
          total: 0,
          resolved: 0,
          avgResolutionTime: 0,
          totalResolutionTime: 0,
          resolvedCount: 0,
          open: 0
        };
      }
      
      categories[category].total++;
      
      if (ticket.status === 'resolved' || ticket.status === 'closed' || ticket.status === 'completed') {
        categories[category].resolved++;
        categories[category].resolvedCount++;
        
        const created = new Date(ticket.created_at || ticket.date_created);
        const resolved = new Date(ticket.resolved_at || ticket.updated_at);
        const resolutionTime = (resolved - created) / (1000 * 60 * 60);
        categories[category].totalResolutionTime += resolutionTime;
      } else {
        categories[category].open++;
      }
    });
    
    return Object.values(categories)
      .map(c => ({
        ...c,
        avgResolutionTime: c.resolvedCount > 0 
          ? Math.round(c.totalResolutionTime / c.resolvedCount) 
          : 0,
        resolutionRate: c.total > 0 
          ? Math.round((c.resolved / c.total) * 100) 
          : 0
      }))
      .sort((a, b) => b.total - a.total);
  };

  const getMonthlyTrend = () => {
    if (!Array.isArray(filteredTickets)) return [];
    
    const months = [];
    const now = new Date();
    
    for (let i = 11; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push({
        month: date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
        created: 0,
        resolved: 0,
        open: 0
      });
    }
    
    filteredTickets.forEach(ticket => {
      const created = new Date(ticket.created_at || ticket.date_created);
      const monthIndex = 11 - (now.getFullYear() - created.getFullYear()) * 12 - (now.getMonth() - created.getMonth());
      
      if (monthIndex >= 0 && monthIndex < 12) {
        months[monthIndex].created++;
        
        if (ticket.status === 'resolved' || ticket.status === 'closed' || ticket.status === 'completed') {
          months[monthIndex].resolved++;
        } else {
          months[monthIndex].open++;
        }
      }
    });
    
    return months;
  };

  // Compute metrics using useMemo for performance
  const metrics = useMemo(() => {
    if (!Array.isArray(filteredTickets)) {
      return {
        totalTickets: 0,
        openTickets: 0,
        resolvedTickets: 0,
        avgResolutionTime: 'N/A',
        resolutionRate: 0,
        slaCompliance: 0
      };
    }
    
    const total = filteredTickets.length;
    const resolved = filteredTickets.filter(t => t.status === 'resolved' || t.status === 'closed' || t.status === 'completed').length;
    const open = filteredTickets.filter(t => t.status !== 'resolved' && t.status !== 'closed' && t.status !== 'completed').length;
    
    const slaPerf = getSLAPerformance();
    const slaCompliance = total > 0 
      ? Math.round((slaPerf.met / (slaPerf.met + slaPerf.breached)) * 100) || 0
      : 0;
    
    return {
      totalTickets: total,
      openTickets: open,
      resolvedTickets: resolved,
      avgResolutionTime: getAverageResolutionTime(),
      resolutionRate: total > 0 ? Math.round((resolved / total) * 100) : 0,
      slaCompliance
    };
  }, [filteredTickets]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading helpdesk reports...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="container mx-auto px-4 py-4">
        {/* Professional Header Card with Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-4">
          {/* Header Row */}
          <div className="flex items-start justify-between mb-4">
            {/* Left: Icon, Title, Description */}
            <div className="flex items-start">
              <div className="flex-shrink-0 rounded-lg p-2.5 mr-3 mt-0.5" 
                   style={{ backgroundColor: 'rgba(0, 135, 212, 0.15)' }}>
                <svg className="h-5 w-5" fill="none" stroke="#0087D4" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-800">Helpdesk Analytics & Reports</h1>
                <p className="text-xs text-gray-500 mb-1">Comprehensive support metrics and performance insights</p>
                <div className="flex items-center mt-1 gap-4">
                  <span className="text-xs font-medium" style={{ color: '#0087D4' }}>
                    {metrics.totalTickets} ticket{metrics.totalTickets !== 1 ? 's' : ''} analyzed
                  </span>
                  <span className="text-xs text-gray-500">
                    {metrics.openTickets} open • {metrics.resolvedTickets} resolved
                  </span>
                </div>
              </div>
            </div>
            
            {/* Right: Export Button */}
            <div className="flex items-center gap-2">
              <button
                onClick={handleExport}
                className="inline-flex items-center justify-center px-4 py-1.5 text-white text-sm font-medium rounded-md shadow-sm hover:opacity-90 transition-all duration-200"
                style={{ 
                  backgroundColor: '#0087D4', 
                  boxShadow: '0 1px 2px rgba(0, 135, 212, 0.3)' 
                }}
              >
                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
                Export
              </button>
            </div>
          </div>

          {/* Filters Row */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 xl:grid-cols-8 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Department</label>
              <SearchableSelect
                options={departments}
                value={filters.department}
                onChange={(value) => handleFilterChange('department', value)}
                disabled={departments.length === 0}
                allText="All Departments"
                getOptionLabel={(dept) => dept.department_name || dept.name}
                getOptionValue={(dept) => dept.department_id || dept.id}
                className="w-full"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Division</label>
              <SearchableSelect
                options={divisions}
                value={filters.division}
                onChange={(value) => handleFilterChange('division', value)}
                disabled={divisions.length === 0}
                allText="All Divisions"
                getOptionLabel={(div) => div.department_name || div.name}
                getOptionValue={(div) => div.department_id || div.id}
                className="w-full"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Status</option>
                <option value="new">New</option>
                <option value="open">Open</option>
                <option value="in_progress">In Progress</option>
                <option value="pending">Pending</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Priority</label>
              <select
                value={filters.priority}
                onChange={(e) => handleFilterChange('priority', e.target.value)}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Priorities</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Category</label>
              <input
                type="text"
                placeholder="Search by category..."
                value={filters.category}
                onChange={(e) => handleFilterChange('category', e.target.value)}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Assignee</label>
              <SearchableSelect
                options={itUsers}
                value={filters.assignee}
                onChange={(value) => handleFilterChange('assignee', value)}
                disabled={itUsers.length === 0}
                allText="All Assignees"
                getOptionLabel={(user) => user.name || user.user_full_name || ''}
                getOptionValue={(user) => String(user.user_id || user.id || '')}
                className="w-full"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Date Range</label>
              <select
                value={timeRange}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setTimeRange(newValue);
                  if (newValue === 'custom') {
                    setShowCustomDateModal(true);
                  }
                }}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="7">Last 7 days</option>
                <option value="30">Last 30 days</option>
                <option value="thisMonth">This month</option>
                <option value="lastMonth">Last month</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>

            {/* Custom Date Range Button */}
            {timeRange === 'custom' && (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Selected Range</label>
                <button
                  onClick={() => setShowCustomDateModal(true)}
                  className="w-full px-2 py-1.5 text-sm border border-blue-300 bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100 focus:ring-blue-500 focus:border-blue-500 flex items-center justify-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span className="truncate">
                    {filters.dateRange.start && filters.dateRange.end 
                      ? `${filters.dateRange.start} to ${filters.dateRange.end}` 
                      : 'Select Range'}
                  </span>
                </button>
              </div>
            )}

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">&nbsp;</label>
              <button
                onClick={() => {
                  setFilters({
                    status: '',
                    priority: '',
                    category: '',
                    assignee: '',
                    requester: '',
                    department: '',
                    division: '',
                    dateRange: { start: '', end: '' }
                  });
                  setTimeRange('30');
                }}
                className="w-full px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-4">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px" aria-label="Tabs">
              {[
                { id: 'overview', label: 'Overview', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
                { id: 'performance', label: 'Performance', icon: 'M13 7h8m0 0v8m0-8l-8 8-4-4-6 6' },
                { id: 'analytics', label: 'Advanced Analytics', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' },
                { id: 'tickets', label: 'Ticket List', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2' }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    group inline-flex items-center py-4 px-6 border-b-2 font-medium text-sm
                    ${activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }
                  `}
                >
                  <svg
                    className={`mr-2 h-5 w-5 ${activeTab === tab.id ? 'text-blue-500' : 'text-gray-400 group-hover:text-gray-500'}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={tab.icon} />
                  </svg>
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <>
            {/* Key Metrics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-600">Total Tickets</h3>
                  <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
                <div className="text-3xl font-bold text-gray-900">{metrics.totalTickets}</div>
                <p className="text-xs text-gray-500 mt-1">All time tickets</p>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-600">Resolution Rate</h3>
                  <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="text-3xl font-bold text-gray-900">{metrics.resolutionRate}%</div>
                <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                  <div className="bg-green-500 h-2 rounded-full transition-all" style={{ width: `${metrics.resolutionRate}%` }}></div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-600">Avg Resolution Time</h3>
                  <svg className="w-8 h-8 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="text-3xl font-bold text-gray-900">{metrics.avgResolutionTime}</div>
                <p className="text-xs text-gray-500 mt-1">Average time to resolve</p>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-600">SLA Compliance</h3>
                  <svg className="w-8 h-8 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <div className="text-3xl font-bold text-gray-900">{metrics.slaCompliance}%</div>
                <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                  <div className="bg-orange-500 h-2 rounded-full transition-all" style={{ width: `${metrics.slaCompliance}%` }}></div>
                </div>
              </div>
            </div>

            {/* Charts Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* Status Distribution */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Status Distribution</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={getStatusDistribution()}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ status, percentage }) => `${status} (${percentage}%)`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="count"
                    >
                      {getStatusDistribution().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={STATUS_COLORS[entry.status] || CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Priority Distribution */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Priority Distribution</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={getPriorityDistribution()}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="priority" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" fill="#3B82F6" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Tickets Trend */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 lg:col-span-2">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Tickets Trend (Last 30 Days)</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={getTicketsTrend()}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Area type="monotone" dataKey="count" stackId="1" stroke="#3B82F6" fill="#3B82F6" fillOpacity={0.6} name="Total" />
                    <Area type="monotone" dataKey="resolved" stackId="2" stroke="#10B981" fill="#10B981" fillOpacity={0.6} name="Resolved" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </>
        )}

        {/* Performance Tab */}
        {activeTab === 'performance' && (
          <>
            {/* Assignee Performance */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Assignee Performance</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assignee</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Resolved</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Avg Time</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Resolution Rate</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {getAssigneePerformance().map((assignee, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{assignee.name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{assignee.total}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{assignee.resolved}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{assignee.avgResolutionTime}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <span className="text-sm font-medium text-gray-900 mr-2">{assignee.resolutionRate}%</span>
                            <div className="w-24 bg-gray-200 rounded-full h-2">
                              <div className="bg-green-500 h-2 rounded-full" style={{ width: `${assignee.resolutionRate}%` }}></div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Charts Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* Category Performance */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Category Performance</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={getCategoryPerformance()}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="category" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="open" fill="#EF4444" name="Open" />
                    <Bar dataKey="resolved" fill="#10B981" name="Resolved" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Resolution Time by Priority */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Avg Resolution Time by Priority</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart data={getResolutionTimeByPriority()}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="priority" />
                    <YAxis yAxisId="left" label={{ value: 'Hours', angle: -90, position: 'insideLeft' }} />
                    <YAxis yAxisId="right" orientation="right" label={{ value: 'Count', angle: 90, position: 'insideRight' }} />
                    <Tooltip />
                    <Legend />
                    <Bar yAxisId="right" dataKey="count" fill="#8B5CF6" name="Ticket Count" />
                    <Line yAxisId="left" type="monotone" dataKey="avgHours" stroke="#F59E0B" strokeWidth={3} name="Avg Hours" />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>

              {/* Monthly Trend */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 lg:col-span-2">
                <h3 className="text-lg font-medium text-gray-900 mb-4">12-Month Trend Analysis</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={getMonthlyTrend()}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="created" stroke="#3B82F6" strokeWidth={2} name="Created" />
                    <Line type="monotone" dataKey="resolved" stroke="#10B981" strokeWidth={2} name="Resolved" />
                    <Line type="monotone" dataKey="open" stroke="#EF4444" strokeWidth={2} name="Open" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </>
        )}

        {/* Advanced Analytics Tab */}
        {activeTab === 'analytics' && (
          <>
            {/* Top Issues */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Top 10 Recurring Issues</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rank</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Issue</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Count</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Percentage</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {getTopIssues().map((issue, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">#{index + 1}</td>
                        <td className="px-6 py-4 text-sm text-gray-900 max-w-md">{issue.issue}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{issue.count}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <span className="text-sm font-medium text-gray-900 mr-2">{issue.percentage}%</span>
                            <div className="w-32 bg-gray-200 rounded-full h-2">
                              <div className="bg-blue-500 h-2 rounded-full" style={{ width: `${issue.percentage}%` }}></div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Charts Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* Hourly Distribution */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Hourly Distribution (Peak Hours)</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={getHourlyDistribution()}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="hour" label={{ value: 'Hour of Day', position: 'insideBottom', offset: -5 }} />
                    <YAxis />
                    <Tooltip />
                    <Area type="monotone" dataKey="count" stroke="#8B5CF6" fill="#8B5CF6" fillOpacity={0.6} name="Tickets Created" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              {/* Daily Distribution */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Daily Distribution (Busiest Days)</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={getDailyDistribution()}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="day" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" fill="#10B981" name="Tickets" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* SLA Performance */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 lg:col-span-2">
                <h3 className="text-lg font-medium text-gray-900 mb-4">SLA Performance Breakdown</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-green-50 rounded-lg p-6 border border-green-200">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-medium text-green-800">SLA Met</h4>
                      <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="text-4xl font-bold text-green-900">{getSLAPerformance().met}</div>
                    <p className="text-sm text-green-700 mt-2">Tickets within SLA</p>
                  </div>

                  <div className="bg-red-50 rounded-lg p-6 border border-red-200">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-medium text-red-800">SLA Breached</h4>
                      <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="text-4xl font-bold text-red-900">{getSLAPerformance().breached}</div>
                    <p className="text-sm text-red-700 mt-2">Tickets exceeded SLA</p>
                  </div>

                  <div className="bg-yellow-50 rounded-lg p-6 border border-yellow-200">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-medium text-yellow-800">Pending</h4>
                      <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="text-4xl font-bold text-yellow-900">{getSLAPerformance().pending}</div>
                    <p className="text-sm text-yellow-700 mt-2">Active tickets in progress</p>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Tickets Tab - Table Only */}
        {activeTab === 'tickets' && (
          <>
          {/* Tickets Table */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Support Tickets</h3>
            </div>        {!Array.isArray(filteredTickets) || filteredTickets.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            {!Array.isArray(filteredTickets) ? 'Loading tickets...' : 'No tickets found matching your criteria.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ticket ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Priority
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Subject
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Requester
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Assignee
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredTickets.map((ticket, index) => (
                  <tr key={ticket.id || index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {ticket.ticket_number || ticket.id || `#${index + 1}`}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getStatusIcon(ticket.status)}
                        <span className={`ml-2 px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(ticket.status)}`}>
                          {ticket.status || 'Unknown'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(ticket.priority)}`}>
                        {ticket.priority || 'Medium'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                      {ticket.subject || ticket.title || 'No Subject'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {ticket.requester || ticket.created_by || 'Unknown'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {ticket.assigned_name || ticket.assignee || 'Unassigned'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDateTime(ticket.created_at || ticket.date_created)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
          </div>
          </>
        )}
      </div>

      {/* Custom Date Range Modal */}
      {showCustomDateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowCustomDateModal(false)}>
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Select Date Range</h3>
              <button
                onClick={() => setShowCustomDateModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                <CustomDatePicker
                  value={filters.dateRange.start ? new Date(filters.dateRange.start).toLocaleDateString('en-GB') : ''}
                  onChange={(date) => {
                    if (date) {
                      const parts = date.split('/');
                      const formatted = `${parts[2]}-${parts[1]}-${parts[0]}`;
                      handleFilterChange('dateRange.start', formatted);
                    } else {
                      handleFilterChange('dateRange.start', '');
                    }
                  }}
                  placeholder="Select start date"
                  placement="left"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                <CustomDatePicker
                  value={filters.dateRange.end ? new Date(filters.dateRange.end).toLocaleDateString('en-GB') : ''}
                  onChange={(date) => {
                    if (date) {
                      const parts = date.split('/');
                      const formatted = `${parts[2]}-${parts[1]}-${parts[0]}`;
                      handleFilterChange('dateRange.end', formatted);
                    } else {
                      handleFilterChange('dateRange.end', '');
                    }
                  }}
                  placeholder="Select end date"
                  placement="left"
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setFilters(prev => ({ ...prev, dateRange: { start: '', end: '' } }));
                  setTimeRange('30');
                  setShowCustomDateModal(false);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Clear
              </button>
              <button
                onClick={() => setShowCustomDateModal(false)}
                disabled={!filters.dateRange.start || !filters.dateRange.end}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default HelpdeskReports;
